// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// NC component (NodeControl Design System). Mirrored from the design system;
// adjust the design system, not this AU copy.
//
// Summarizes a set of related counts or field values as a group of small,
// scannable tiles inside a single bordered panel — for example, a set of
// record counts by outcome, or a device's key identifying fields
// (nc-components/usage-notes/stat-tile-grid.md). This is now an active NC
// component with real usage in AU0001, AU0004, and (via this file) AU0009.
//
// Title styling: text-nc-3/font-nc-2/leading-nc-1, the same rung as
// NCSectionHeader's own title (typography-scale-usage.md, "Section title").

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface NCStatTileGridProps {
  /** The group's own title, e.g. "Device", "Logs" — rendered as this
   *  panel's own first child, not floated above it. */
  title: string;
  children: ReactNode;
  className?: string;
  /** Override the tile grid's column layout if the default doesn't fit. */
  gridClassName?: string;
}

export function NCStatTileGrid({
  title,
  children,
  className,
  gridClassName,
}: NCStatTileGridProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-nc-3 rounded-lg border border-border bg-card p-nc-5",
        className,
      )}
    >
      <h2 className="text-nc-3 font-nc-2 leading-nc-1 tracking-tight text-foreground">
        {title}
      </h2>
      <div className={cn("grid grid-cols-2 gap-nc-3 sm:grid-cols-4", gridClassName)}>
        {children}
      </div>
    </div>
  );
}

export interface NCStatTileProps {
  children: ReactNode;
  className?: string;
}

/**
 * One tile inside the grid. No border or bg-card of its own — the outer
 * panel's bg-card vs. this bg-background is what separates tiles visually;
 * giving a tile its own border or bg-card breaks that contrast.
 *
 * `group/tile` is a bare hover hook for content that wants a hover-revealed
 * affordance scoped to this one tile (e.g. `Field`'s `copyable` copy button)
 * — it has no visual effect of its own on tiles that don't use it.
 */
export function NCStatTile({ children, className }: NCStatTileProps) {
  return (
    <div className={cn("group/tile flex flex-col gap-nc-2 rounded-md bg-background p-nc-4", className)}>
      {children}
    </div>
  );
}
